import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeftIcon, BookOpenIcon } from "@heroicons/react/24/outline";
import RoleOnly from "../../../routes/roleOnly";
import { OfferGuideSection } from "../../../components/dashboard/companyGuide/OfferGuideSection";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24 border-b border-slate-100 py-8 last:border-b-0">
    <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>
  </section>
);

export const UserGuideOffersGettingStarted = () => {
  const { t } = useTranslation();
  const location = useLocation();

  useEffect(() => {
    document.title = `${t("user_guide.offers.title")} | Promang`;
  }, [t]);

  useEffect(() => {
    const raw = location.hash?.replace(/^#/, "");
    if (!raw) return;
    const el = document.getElementById(raw);
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6 lg:px-8">
        <Link
          to="/dashboard/guide"
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-slate-900"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          {t("user_guide.offers.back_to_hub")}
        </Link>

        <div className="rounded-[2rem] border border-slate-200 bg-white px-6 py-8 shadow-sm sm:px-8">
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-slate-600">
            <BookOpenIcon className="h-4 w-4" />
            {t("user_guide.offers.badge")}
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-950">{t("user_guide.offers.title")}</h1>
          <p className="mt-3 text-sm text-slate-600">{t("user_guide.offers.purpose")}</p>

          <div className="mt-6">
            <RoleOnly role="admin">
              <OfferGuideSection />
            </RoleOnly>
          </div>

          <Section id="workflow" title={t("user_guide.offers.section.workflow.title")}>
            <p>{t("user_guide.offers.section.workflow.p1")}</p>
            <p>{t("user_guide.offers.section.workflow.p2")}</p>
          </Section>

          <Section id="calculation" title={t("user_guide.offers.section.calculation.title")}>
            <p>{t("user_guide.offers.section.calculation.p1")}</p>
            <ul className="list-inside list-disc space-y-2">
              <li>{t("user_guide.offers.section.calculation.li1")}</li>
              <li>{t("user_guide.offers.section.calculation.li2")}</li>
              <li>{t("user_guide.offers.section.calculation.li3")}</li>
            </ul>
          </Section>

          <Section id="document" title={t("user_guide.offers.section.document.title")}>
            <p>{t("user_guide.offers.section.document.p1")}</p>
          </Section>

          <Section id="related" title={t("user_guide.offers.section.related.title")}>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/dashboard/offers"
                className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                {t("user_guide.offers.section.related.link_list")}
              </Link>
              <Link
                to="/dashboard/offers/settings"
                className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-sm font-semibold text-slate-800 hover:bg-slate-50"
              >
                {t("user_guide.offers.section.related.link_settings")}
              </Link>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
};
